import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OpsRider } from '../queries';
import { api, errorMessage } from '../../api';
import { ErrorState, LineDot, Loading, StatusPill, useNow, useTitle } from '../../ui/bits';
import { useToast } from '../../ui/toast';
import { Icon } from '../../ui/Icon';
import { DotMeter, Fig, PageHead, Stat } from '../../ui/viz';
import { count, paymentLabel, peso } from '../../lib/format.js';
import { normaliseBoard } from '../../lib/board.js';
import { useOpsBoard, useOpsRiders, useOpsSettings } from '../queries';
import { GaugeRing, RidersMap } from '../parts';
import { RiderProfileDrawer } from './ProfileDrawer';
import './riders.css';

interface Waiting {
  deliveryId: string;
  orderId: string;
  code: string;
  seq: number;
  pickups: string[];
  ticket: ReturnType<typeof normaliseBoard>[number]['orders'][number];
}

/** A rider's load as pills: one per delivery on the road (max 3 drawn). */
function Load({ n }: { n: number }) {
  return (
    <span className="rd-load" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <i key={i} className={i < n ? 'on' : ''} />
      ))}
    </span>
  );
}

/**
 * Riders & dispatch. Phase 2 rule: a delivery reaches Ops when all its shop orders are ready.
 * The board's Ready column gives order ids; each order's detail gives its deliveries, and the
 * still-`pending` ones are the ones waiting for a rider.
 */
export function RidersPage() {
  useTitle('Riders & dispatch');
  const qc = useQueryClient();
  const toast = useToast();
  const board = useOpsBoard();
  const riders = useOpsRiders();
  const settings = useOpsSettings();
  const now = useNow(1000);
  const ready = useMemo(() => normaliseBoard(board.data).find((c) => c.stage === 'ready')?.orders ?? [], [board.data]);

  // Board cards carry their deliveries (contract: OpsBoardColumn.orders[].deliveries), so the
  // ones waiting for a rider come straight from the board: no per-order lookups.
  const waiting: Waiting[] = [];
  for (const o of ready) {
    for (const del of o.deliveries ?? []) {
      if (del.status !== 'pending' || !del.dispatchable) continue;
      waiting.push({ deliveryId: del.deliveryId, orderId: o.orderId, code: o.code, seq: del.seq, pickups: o.shopNames, ticket: o });
    }
  }
  waiting.sort((a, b) => Number(b.ticket.late) - Number(a.ticket.late) || b.ticket.minutes - a.ticket.minutes);

  const [deliveryId, setDeliveryId] = useState<string | null>(null);
  const [riderId, setRiderId] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const selected = waiting.find((w) => w.deliveryId === deliveryId) ?? null;
  const all = riders.data ?? [];
  const online = all.filter((r) => r.online).sort((a, b) => a.activeDeliveries - b.activeDeliveries || a.name.localeCompare(b.name));
  const offline = all.filter((r) => !r.online);
  const free = online.filter((r) => r.activeDeliveries === 0).length;
  const busy = online.length - free;
  const rider = online.find((r) => r.userId === riderId) ?? null;
  const cashLimit = Number((settings.data?.cod as Record<string, unknown> | undefined)?.riderCashLimitCentavos ?? 0) || null;
  const over = (r: OpsRider) => cashLimit !== null && r.cashOnHandCentavos > cashLimit;
  const cash = all.reduce((s, r) => s + Math.max(0, r.cashOnHandCentavos), 0);
  const overCount = all.filter(over).length;
  const lateWaiting = waiting.filter((w) => w.ticket.late).length;
  const ago = board.dataUpdatedAt ? Math.max(0, Math.round((now - board.dataUpdatedAt) / 1000)) : null;

  const assign = useMutation({
    mutationFn: (v: { deliveryId: string; riderUserId: string }) => api.assignDelivery(v.deliveryId, v.riderUserId),
    onSuccess: () => {
      toast(`${selected?.code ?? 'Delivery'} assigned to ${rider?.name ?? 'the rider'}.`);
      setDeliveryId(null);
      setRiderId(null);
      void qc.invalidateQueries({ queryKey: ['ops'] });
    },
    onError: (err) => toast(errorMessage(err, "Couldn't assign. Try again."), 'error'),
  });

  return (
    <>
      <PageHead title="Riders & dispatch" live meta={`Live${ago !== null ? ` · ${ago}s` : ''}`} />

      <div className="dv-stats lx-stats">
        <Stat label="Online" value={riders.data ? `${online.length} / ${all.length}` : '—'} sub={busy ? `${busy} on a delivery` : undefined} visual={<DotMeter total={all.length} on={online.length} busy={busy} />} />
        <Stat
          label="Free now"
          value={riders.data ? count(free) : '—'}
          visual={
            <GaugeRing pct={online.length ? Math.round((free / online.length) * 100) : null} tone={free ? 'good' : online.length ? 'warn' : 'good'}>
              <Icon name="person" />
            </GaugeRing>
          }
        />
        <Stat
          label="Waiting"
          value={board.data ? count(waiting.length) : '—'}
          tone={lateWaiting ? 'alert' : undefined}
          sub={lateWaiting ? <span className="dv-chip bad">{lateWaiting} late</span> : undefined}
          visual={
            <span className="dv-disc" aria-hidden="true">
              <Icon name="bag" />
            </span>
          }
        />
        <Stat
          label="Cash on hand"
          value={riders.data ? peso(cash) : '—'}
          tone={overCount ? 'alert' : undefined}
          sub={overCount ? <span className="dv-chip bad">{overCount} to remit</span> : cashLimit !== null ? `Limit ${peso(cashLimit)}` : undefined}
          visual={
            <span className="dv-disc" aria-hidden="true">
              <Icon name="cash" />
            </span>
          }
        />
      </div>

      <section className="card dv-card rd-console" aria-label="Dispatch">
        <div className="rd-cols">
          <div className="rd-pane" role="group" aria-labelledby="wait-h">
            <div className="dv-card-h">
              <h2 id="wait-h">Waiting for a rider</h2>
              {lateWaiting ? <span className="dv-chip bad">{lateWaiting} late</span> : null}
              <span className="grow" />
              <Fig text={count(waiting.length)} size="sm" animate={false} />
            </div>
            {board.isError ? (
              <ErrorState error={board.error} onRetry={() => void board.refetch()} />
            ) : board.isLoading ? (
              <Loading label="Loading deliveries" rows={2} />
            ) : waiting.length ? (
              <div role="radiogroup" aria-label="Delivery to assign" className="rd-list">
                {waiting.map((w) => {
                  const t = w.ticket;
                  return (
                    <button
                      key={w.deliveryId}
                      type="button"
                      role="radio"
                      aria-checked={w.deliveryId === deliveryId}
                      className={`ticket rd-q${w.deliveryId === deliveryId ? ' sel' : t.late ? ' late' : ''}`}
                      onClick={() => setDeliveryId(w.deliveryId)}
                    >
                      <span className="rd-q-top">
                        <span className="lb-code">
                          {w.code}
                          {w.seq > 1 ? <span className="rd-seq"> · {w.seq}</span> : null}
                        </span>
                        <span className="dv-lines" aria-label={t.lines.join(', ')}>
                          {t.lines.map((l) => (
                            <LineDot key={l} line={l} />
                          ))}
                          {t.speed === 'express' ? (
                            <span className="lb-bolt" title="Express">
                              <Icon name="bolt" size="sm" />
                            </span>
                          ) : null}
                        </span>
                        <span className="grow" />
                        {t.late ? <span className="dv-chip bad">{t.lateBy ? `+${t.lateBy}m` : 'Late'}</span> : <span className="lb-min">{t.minutes}m</span>}
                      </span>
                      <span className="rd-route">
                        <span className="rd-stop" title="Pick up">
                          <Icon name="bag" size="sm" />
                          <span className="sr-only">Pick up: </span>
                          {w.pickups.join(' → ')}
                        </span>
                        <span className="rd-stop" title="Drop off">
                          <Icon name="pin" size="sm" />
                          <span className="sr-only">Drop off: </span>
                          {t.addressShort}
                        </span>
                      </span>
                      <span className="rd-q-foot">
                        <span className={t.payment === 'cod' ? 'lb-pay cod' : 'lb-pay'}>{paymentLabel(t.payment)}</span>
                        <Fig text={peso(t.totalCentavos)} size="sm" animate={false} />
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="dv-quiet rd-quiet">
                <Icon name="check" />
                All caught up
              </div>
            )}
          </div>

          <div className="rd-pane" role="group" aria-labelledby="online-h">
            <div className="dv-card-h">
              <h2 id="online-h">Online riders</h2>
              {free ? <span className="dv-chip good">{free} free</span> : null}
              <span className="grow" />
              <Fig text={count(online.length)} size="sm" animate={false} />
            </div>
            {riders.isError ? (
              <ErrorState error={riders.error} onRetry={() => void riders.refetch()} />
            ) : !riders.data ? (
              <Loading label="Loading riders" rows={2} />
            ) : online.length ? (
              <div role="radiogroup" aria-label="Rider" className="rd-list">
                {online.map((r) => (
                  <button key={r.userId} type="button" role="radio" aria-checked={r.userId === riderId} className={r.userId === riderId ? 'ticket rd-r sel' : 'ticket rd-r'} onClick={() => setRiderId(r.userId)}>
                    <span className={r.activeDeliveries ? 'rd-st busy' : 'rd-st'} aria-hidden="true" />
                    <span className="rd-who">
                      <b>{r.name}</b>
                      <span className="rd-state">
                        <Load n={r.activeDeliveries} />
                        {r.activeDeliveries ? `${r.activeDeliveries} on the road` : 'Free'}
                      </span>
                    </span>
                    <span className="rd-cash">
                      <Fig text={peso(r.cashOnHandCentavos)} size="sm" animate={false} />
                      {over(r) ? <span className="dv-chip bad">Over limit</span> : <span className="rd-cash-l">Cash</span>}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="dv-quiet rd-quiet">
                <Icon name="person" />
                No riders online
              </div>
            )}
          </div>
        </div>

        <div className="rd-bar">
          <div className="rd-pick" aria-hidden="true">
            <span className={selected ? 'rd-slot on' : 'rd-slot'}>{selected ? selected.code : 'Order'}</span>
            <Icon name="arrow" size="sm" />
            <span className={rider ? 'rd-slot on' : 'rd-slot'}>{rider ? rider.name : 'Rider'}</span>
          </div>
          <p className="rd-hint" aria-live="polite">
            {selected && rider ? `${rider.name} will pick up ${selected.code} from ${selected.pickups.join(' then ')}.` : 'Pick a delivery, then a rider.'}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!selected || !rider || assign.isPending}
            aria-busy={assign.isPending || undefined}
            onClick={() => selected && rider && assign.mutate({ deliveryId: selected.deliveryId, riderUserId: rider.userId })}
          >
            <Icon name="pin" />
            {assign.isPending ? 'Assigning…' : selected && rider ? `Assign ${rider.name} to ${selected.code}` : 'Assign rider'}
          </button>
        </div>
      </section>

      <div className="dv-grid">
        <section className="card dv-card dv-span-7" aria-labelledby="all-h">
          <div className="dv-card-h">
            <h2 id="all-h">All riders</h2>
            {riders.data ? <span className="dv-chip">{all.length}</span> : null}
          </div>
          {riders.data ? (
            all.length ? (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Rider</th>
                      <th scope="col">Status</th>
                      <th scope="col" className="num">On the road</th>
                      <th scope="col" className="num">Cash on hand</th>
                      <th scope="col">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...online, ...offline].map((r) => (
                      <tr key={r.userId}>
                        <td>
                          <b>{r.name}</b>
                        </td>
                        <td>{r.online ? r.activeDeliveries ? <StatusPill tone="info">Busy</StatusPill> : <StatusPill tone="success">Online</StatusPill> : <StatusPill tone="neutral">Offline</StatusPill>}</td>
                        <td className="num">{count(r.activeDeliveries)}</td>
                        <td className="num">
                          {over(r) ? (
                            <>
                              <StatusPill tone="error">Remit</StatusPill>{' '}
                            </>
                          ) : null}
                          {peso(r.cashOnHandCentavos)}
                        </td>
                        <td className="num">
                          <button type="button" className="btn btn-quiet" onClick={() => setProfileId(r.userId)} aria-label={`Open ${r.name}'s profile`}>
                            Profile
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dv-quiet" title="Riders appear here once they sign up in the rider app.">
                No riders yet
              </div>
            )
          ) : (
            <Loading label="Loading riders" />
          )}
        </section>
        <section className="card dv-card dv-span-5" aria-labelledby="map-h">
          <div className="dv-card-h">
            <h2 id="map-h">Where they are</h2>
          </div>
          {riders.data ? <RidersMap riders={all} quietEmpty /> : <Loading label="Loading map" />}
        </section>
      </div>

      <RiderProfileDrawer
        userId={profileId}
        name={all.find((r) => r.userId === profileId)?.name ?? null}
        rider={all.find((r) => r.userId === profileId) ?? null}
        cashLimit={cashLimit}
        onClose={() => setProfileId(null)}
      />
    </>
  );
}
